import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { getMessagingRecipients } from "../../services/messaging";
import { getTeams } from "../../services/teams";
import { getOrgTagDefinitions } from "../../services/prospects";
import type { MessagingContactSummary } from "../../types/messaging";
import type { TeamResponse } from "../../types/api";
import type { DefaultTagDefinition } from "../../types/tags";
import { contactKey } from "./contactKey";

interface RecipientPickerProps {
  /** Insertion-ordered, so the send queue matches what the coach was looking at. */
  selected: Map<string, MessagingContactSummary>;
  onChange: (next: Map<string, MessagingContactSummary>) => void;
}

/**
 * Search-and-pick for message recipients, with a whole-team shortcut.
 *
 * Contacts with no phone number are listed rather than hidden. Dropping them would make a roster
 * look shorter than it is, and the send preview already reports them as unsendable with a reason —
 * seeing "no phone" next to a name is how a coach finds out the number is missing.
 */
export const RecipientPicker = ({ selected, onChange }: RecipientPickerProps) => {
  const [search, setSearch] = useState("");
  const [teamId, setTeamId] = useState("");
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [contacts, setContacts] = useState<MessagingContactSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchId = useId();
  const teamInputId = useId();
  const teamListId = useId();
  const resultsLabelId = useId();

  const [tagDefinitions, setTagDefinitions] = useState<DefaultTagDefinition[]>([]);
  // tag name -> selected values. OR within a tag, AND across tags, matching the roster page.
  const [tagFilters, setTagFilters] = useState<Record<string, string[]>>({});
  const [teamQuery, setTeamQuery] = useState("");
  const [teamListOpen, setTeamListOpen] = useState(false);

  useEffect(() => {
    // Best effort: without these the picker still works by name search.
    getTeams()
      .then(setTeams)
      .catch(() => setTeams([]));
    getOrgTagDefinitions()
      .then(setTagDefinitions)
      .catch(() => setTagDefinitions([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Debounced so typing a name does not fire a request per keystroke.
    const timer = setTimeout(() => {
      getMessagingRecipients(search || undefined, 100, teamId || undefined, tagFilters)
        .then((rows) => {
          if (cancelled) return;
          setContacts(rows);
          setError(null);
        })
        .catch(() => {
          if (!cancelled) setError("Could not load recipients.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, teamId, tagFilters]);

  const toggle = useCallback(
    (contact: MessagingContactSummary) => {
      const next = new Map(selected);
      const key = contactKey(contact);
      if (next.has(key)) next.delete(key);
      else next.set(key, contact);
      onChange(next);
    },
    [selected, onChange]
  );

  const addAllVisible = useCallback(() => {
    const next = new Map(selected);
    contacts.forEach((c) => next.set(contactKey(c), c));
    onChange(next);
  }, [contacts, selected, onChange]);

  const toggleTagValue = useCallback((tagName: string, value: string) => {
    setTagFilters((current) => {
      const existing = current[tagName] ?? [];
      const next = existing.includes(value)
        ? existing.filter((v) => v !== value)
        : [...existing, value];

      // Drop the key entirely when nothing is left, so an empty array never reaches the server
      // as a filter that matches nobody.
      const updated = { ...current };
      if (next.length === 0) delete updated[tagName];
      else updated[tagName] = next;
      return updated;
    });
  }, []);

  const activeTagCount = Object.values(tagFilters).reduce((n, v) => n + v.length, 0);

  const selectedTeamName = teams.find((t) => t.id === teamId)?.name;

  const matchingTeams = useMemo(() => {
    const term = teamQuery.trim().toLowerCase();
    // While a team is chosen its name sits in the input; that must not filter the list down
    // to just itself when the coach reopens it to switch.
    if (!term || term === selectedTeamName?.toLowerCase()) return teams;
    return teams.filter((t) => t.name.toLowerCase().includes(term));
  }, [teams, teamQuery, selectedTeamName]);

  return (
    <div className="compose-modal__picker">
      <div className="compose-modal__picker-controls">
        <label htmlFor={teamInputId} className="compose-modal__picker-label">
          Team
        </label>
        {/*
          A combobox rather than a <select>: an org with sixty travel teams cannot find one
          in a native dropdown. Options are <button>s so they are reachable by keyboard
          without hand-rolling arrow-key navigation on a non-interactive element.
        */}
        <div className="compose-modal__combobox">
          <input
            id={teamInputId}
            type="text"
            role="combobox"
            aria-expanded={teamListOpen}
            aria-controls={teamListId}
            aria-autocomplete="list"
            value={teamQuery}
            placeholder="All players"
            onFocus={() => setTeamListOpen(true)}
            onChange={(e) => {
              setTeamQuery(e.target.value);
              setTeamListOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") setTeamListOpen(false);
            }}
          />
          {teamId !== "" && (
            <button
              type="button"
              className="compose-modal__combobox-clear"
              onClick={() => {
                setTeamId("");
                setTeamQuery("");
                setTeamListOpen(false);
              }}
              aria-label="Show all players instead of one team"
            >
              &times;
            </button>
          )}
          {teamListOpen && (
            <ul className="compose-modal__combobox-list" id={teamListId} role="listbox">
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={teamId === ""}
                  onClick={() => {
                    setTeamId("");
                    setTeamQuery("");
                    setTeamListOpen(false);
                  }}
                >
                  All players
                </button>
              </li>
              {matchingTeams.map((team) => (
                <li key={team.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={teamId === team.id}
                    onClick={() => {
                      setTeamId(team.id);
                      setTeamQuery(team.name);
                      setTeamListOpen(false);
                    }}
                  >
                    {team.name}
                  </button>
                </li>
              ))}
              {matchingTeams.length === 0 && (
                <li className="compose-modal__combobox-empty">No team matches that.</li>
              )}
            </ul>
          )}
        </div>

        <label htmlFor={searchId} className="compose-modal__picker-label">
          Search
        </label>
        <input
          id={searchId}
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Name"
        />

        <button
          type="button"
          onClick={addAllVisible}
          disabled={contacts.length === 0}
          aria-label={
            selectedTeamName
              ? `Add everyone on ${selectedTeamName}`
              : "Add everyone shown"
          }
        >
          {selectedTeamName ? `Add all of ${selectedTeamName}` : "Add all shown"}
        </button>
      </div>

      {tagDefinitions.length > 0 && (
        <div className="compose-modal__tags">
          <span className="compose-modal__picker-label">
            Filter by tag{activeTagCount > 0 ? ` (${activeTagCount})` : ""}
          </span>
          {activeTagCount > 0 && (
            <button
              type="button"
              className="compose-modal__tags-clear"
              onClick={() => setTagFilters({})}
            >
              Clear tag filters
            </button>
          )}
          {tagDefinitions.map((tag) => (
            /*
              role="group" with a <span> label rather than a <label> wrapping the set: a label
              naming several controls is wrong for screen readers and trips the a11y ratchet.
            */
            <div
              key={tag.tagName}
              className="compose-modal__tag-row"
              role="group"
              aria-label={tag.tagName}
            >
              <span className="compose-modal__tag-name">{tag.tagName}</span>
              <div className="compose-modal__tag-values">
                {tag.options.map((option) => {
                  const active = (tagFilters[tag.tagName] ?? []).includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      className={`compose-modal__tag-value${
                        active ? " compose-modal__tag-value--active" : ""
                      }`}
                      aria-pressed={active}
                      aria-label={`${tag.tagName}: ${option}`}
                      onClick={() => toggleTagValue(tag.tagName, option)}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <p className="compose-modal__tags-hint">
            Picking more than one value in a tag matches any of them. Picking values in different
            tags narrows to people who match all of them.
          </p>
        </div>
      )}

      {selected.size > 0 && (
        <ul className="compose-modal__chips" aria-label="Selected recipients">
          {[...selected.values()].map((contact) => (
            <li key={contactKey(contact)} className="compose-modal__chip">
              {contact.displayName}
              {contact.contactType === "Guardian" && (
                <span className="compose-modal__chip-kind">guardian</span>
              )}
              <button
                type="button"
                onClick={() => toggle(contact)}
                aria-label={`Remove ${contact.displayName}`}
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}

      <div role="status" aria-live="polite" className="compose-modal__announcer">
        {error ??
          (loading
            ? null
            : `${contacts.length} ${contacts.length === 1 ? "person" : "people"} shown, ${selected.size} selected.`)}
      </div>

      <span id={resultsLabelId} className="compose-modal__picker-label">
        Add recipients
      </span>
      <ul
        className="compose-modal__results"
        role="group"
        aria-labelledby={resultsLabelId}
      >
        {loading && contacts.length === 0 ? (
          <li className="compose-modal__results-empty">Loading...</li>
        ) : contacts.length === 0 ? (
          <li className="compose-modal__results-empty">Nobody matches that.</li>
        ) : (
          contacts.map((contact) => {
            const key = contactKey(contact);
            const isSelected = selected.has(key);
            return (
              <li key={key}>
                <label className="compose-modal__result">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(contact)}
                  />
                  <span className="compose-modal__result-name">
                    {contact.displayName}
                  </span>
                  {contact.contactType === "Guardian" && (
                    <span className="compose-modal__chip-kind">guardian</span>
                  )}
                  <span className="compose-modal__result-phone">
                    {contact.phoneNumber ?? "no phone"}
                  </span>
                </label>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
};
